import Player from '@vimeo/player';

/**
 * The Video class works in conjunction with the video snippet. It scrapes data
 * from the snippet and instantiates a custom Vimeo player on that data.
 * It handles custom play/pause and mute/unmute buttons
 */
export default class Video {
  /**
   * @param {DOMnode} element top-level node for the section/component
   * @param {DOMnode} video top-level node for the video
   * @param {Class} layout for handling breakpoints
   */
  constructor({ element, video, layout }) {
    const dataEl = video.querySelector('[data-component-data]');
    
    this.element = element;
    this.layout = layout;
    this.data = dataEl ? JSON.parse(dataEl.textContent) : {};
    
    this.isMuted = true;
    this.isMobile = true;
    this.isPlaying = this.data.autoplay;
    this.hasMultipleVideos = !!this.data.videoDesktop && !!this.data.videoMobile;

    this.handlePlay = this.handlePlay.bind(this);
    this.handleMute = this.handleMute.bind(this);
    this.handleBpChange = this.handleBpChange.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.playEl = this.data.play.selector
      ? element.querySelector(this.data.play.selector)
      : video.querySelector('[data-video-play]');
    this.muteEl = this.data.mute.selector
      ? element.querySelector(this.data.mute.selector)
      : video.querySelector('[data-video-mute]');

    this.init();
  }

  handlePlay() {
    if (this.isPlaying) {
      this.pauseVideo();
    } else this.playVideo();
    if (this.playEl) [...this.playEl.children].forEach((child) => child.classList.toggle('hidden'));
  }

  pauseVideo() {
    this.player.pause();
    this.isPlaying = false;
    this.playEl?.setAttribute('aria-label', this.data.play.playText);
  }

  playVideo() {
    this.player.play();
    this.isPlaying = true;
    this.playEl?.setAttribute('aria-label', this.data.play.pauseText);
  }

  handleMute() {
    if (this.isMuted) {
      this.unmuteVideo();
    } else {
      this.muteVideo();
    }

    if (this.muteEl) [...this.muteEl.children].forEach((child) => child.classList.toggle('hidden'));
  }

  unmuteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = false;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(1);
    }
    this.isMuted = false;
    this.muteEl?.setAttribute('aria-label', this.data.mute.muteText);
  }

  muteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = true;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(0);
    }
    this.isMuted = true;
    this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
  }

  resetPlayer() {
    this.unbindEventListeners();

    if (this.data.host === 'shopify') {
      this.resetShopifyPlayer();
    } else if (this.data.host === 'vimeo') {
      this.resetVimeoPlayer();
    }
  }

  unbindEventListeners() {
    this.playEl?.removeEventListener('click', this.handlePlay);
    this.muteEl?.removeEventListener('click', this.handleMute);
    if (this.layout) {
      window.removeEventListener('bpChange', this.handleBpChange);
      window.removeEventListener('resize', this.handleResize);
    }
  }

  resetShopifyPlayer() {
    this.resetControlButtons();
  }

  resetVimeoPlayer() {
    this.player.destroy().then(() => {
      this.resetControlButtons();
      this.init();
    });
  }

  resetControlButtons() {
    this.isPlaying = this.data.autoplay;
    this.playEl?.setAttribute('aria-label', this.data.autoplay ? this.data.play.pauseText : this.data.play.playText);
    this.playEl?.children[this.data.autoplay ? 1 : 0].classList.remove('hidden');
    this.playEl?.children[this.data.autoplay ? 0 : 1].classList.add('hidden');

    this.isMuted = true;
    this.muteEl?.setAttribute('aria-label', this.data.mute.unmuteText);
    this.muteEl?.children[0].classList.remove('hidden');
    this.muteEl?.children[1].classList.add('hidden');
  }

  updateAriaControls(videoContainerId) {
    this.playEl?.setAttribute('aria-controls', videoContainerId);
    this.muteEl?.setAttribute('aria-controls', videoContainerId);
  }

  handleBpChange() {
    if (this.isMobile && this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else if (!this.isMobile && !this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  handleResize() {
    if (this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  init() {    
    if (this.layout && this.hasMultipleVideos) {
      if (this.layout.isAboveBreakpoint('sm')) {
        this.initVideoByType(this.data.videoDesktop);
        this.isMobile = false;
      } else {
        this.initVideoByType(this.data.videoMobile);
        this.isMobile = true;
      }
    } else if (this.data.videoDesktop) {
        this.initVideoByType(this.data.videoDesktop);
    } else if (this.data.videoMobile) {
      this.initVideoByType(this.data.videoMobile);
    }
  }

  initVideoByType(video) {    
    if (this.data.host === 'shopify') {
      this.initShopifyVideo(video.id);
    } else if (this.data.host === 'vimeo') {
      this.initVimeoVideo(video);
    }
  }

  calculateVideoHeight() {
    let height = this.element.offsetHeight;
    if (height < this.element.offsetWidth) {
      height *= this.element.offsetWidth / height;
    }
    return height;
  }

  onTimeUpdate = (vimeoPlayer) => {
    vimeoPlayer.getCurrentTime().then((currentTime) => {
      const roundedTime = Math.floor(currentTime);
      // Detect if the video is playing based on the roundedTime value
      if (roundedTime > 0) {
        // If the video is playing, add a class to the video container if its present
        const videoContainer = this.element.querySelector('.hero-secondary__video');
        if (videoContainer && !videoContainer.classList.contains('video-playing')) {
          videoContainer.classList.add('video-playing');
        }
      }
    });
  };

  initVimeoVideo(id) {
    const elementId = `video-${this.data.prefix}${id}`;
    
    const options = {
      id,
      controls: false,
      autoplay: this.data.autoplay,
      muted: true,
      responsive: true,
      loop: true,
    };

    this.player = new Player(elementId, options);
    this.updateAriaControls(elementId);
    this.player.on('timeupdate', () => this.onTimeUpdate(this.player));
    this.bindEventListeners();
  }

  initShopifyVideo(id) {
    // Try first in direct selector (original way)
    this.player = this.element.querySelector(`#video-${id} video`);
    
    // If not found, try to find video element in any container with ID video-[id]
    if (!this.player) {
      const container = this.element.querySelector(`#video-${id}`);
      if (container) {
        this.player = container.querySelector('video');
      }
    }
    
    // Final fallback: try to find ANY video element in the component
    if (!this.player) {
      this.player = this.element.querySelector('video');
    }
    
    if (this.player) {
      const containerId = `video-${id}`;
      this.updateAriaControls(containerId);

      // Ensure proper attributes for Safari mobile
      this.player.setAttribute('playsinline', true);
      this.player.setAttribute('webkit-playsinline', true);

      // For iOS Safari specifically
      if (this.data.autoplay) {
        // Need to ensure video is properly loaded before attempting to play
        this.player.addEventListener('loadedmetadata', () => {
          // Delay play slightly to ensure Safari has fully loaded the video
          setTimeout(() => {
            const playPromise = this.player.play();
            
            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // Auto-play was prevented, try once more with user interaction
                this.isPlaying = false;
              });
            }
          }, 100);
        }, { once: true });
      }
      
      this.bindEventListeners();
    }
  }

  bindEventListeners() {
    this.playEl?.addEventListener('click', this.handlePlay);
    this.muteEl?.addEventListener('click', this.handleMute);
    if (this.layout && this.hasMultipleVideos) {
      if (this.data.mobileFullHeight) {
        window.addEventListener('resize', () => {
          if (this.timerId) clearTimeout(this.timerId);
          this.timerId = setTimeout(() => {
            this.handleResize();
          }, 500);
        });
      } else {
        window.addEventListener('bpChange', this.handleBpChange);
      }
    }
  }
}
